/**
 * OIN catalog search results for the Current task panel (not the hidden chat stream).
 * "Add to Okta" sends a PIVOT user message so the architect runs executeAction after config questions.
 */
function formatSignOnMode(mode) {
  if (mode == null || mode === "") return "";
  const s = String(mode);
  if (s === "SAML_2_0") return "SAML 2.0";
  return s.replace(/_/g, " ");
}

export default function OINResultsDisplay({ ui, busy = false, onAddToOkta = null, addingAppId = null }) {
  const status = ui?.status ?? "idle";
  const apps = Array.isArray(ui?.apps) ? ui.apps : [];
  const searchPerformed = Boolean(ui?.searchPerformed);
  const lastQuery = ui?.lastQuery != null ? String(ui.lastQuery) : "";
  const errorMessage = ui?.errorMessage != null ? String(ui.errorMessage) : "";
  const catalogNote = ui?.catalogNote != null ? String(ui.catalogNote) : "";

  if (busy || status === "loading") {
    return (
      <div
        className="mt-3 rounded-lg border border-[rgb(var(--idaxis-accent-primary-rgb)/0.25)] bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-3 py-3 text-sm text-slate-300"
        role="status"
        aria-live="polite"
      >
        Searching OIN catalog…
      </div>
    );
  }

  if (status === "rate_limited" && searchPerformed) {
    return (
      <div
        className="mt-3 rounded-lg border border-amber-500/35 bg-amber-500/[0.1] px-3 py-3 text-sm text-amber-50/95"
        role="status"
        aria-live="polite"
      >
        {errorMessage || "Backend is cooling down. Try again in a few moments."}
      </div>
    );
  }

  if (status === "error" && searchPerformed) {
    return (
      <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/[0.08] px-3 py-3 text-sm text-rose-100/95">
        {errorMessage || "Catalog search failed."}
      </div>
    );
  }

  if (status === "done" && searchPerformed) {
    if (apps.length === 0) {
      return (
        <p className="mt-3 text-center text-sm text-secondary">
          No apps found for &apos;{lastQuery}&apos; — try a different search term
        </p>
      );
    }

    return (
      <div className="mt-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Results</p>
        {catalogNote ? (
          <p className="mt-1 text-[11px] text-slate-500">{catalogNote}</p>
        ) : null}
        <div className="mt-2 max-h-[min(420px,50vh)] space-y-2 overflow-y-auto pr-0.5">
          {apps.slice(0, 50).map((app, i) => {
            const rowKey = app?.id != null ? String(app.id) : app?.name != null ? String(app.name) : `i-${i}`;
            const displayName =
              (app?.displayName != null && String(app.displayName).trim()) ||
              (app?.label != null && String(app.label).trim()) ||
              (app?.name != null ? String(app.name) : "(app)");
            const nameKey = app?.name != null ? String(app.name) : "";
            const isAdding = addingAppId != null && String(addingAppId) === rowKey;
            const cat = app?.category ? String(app.category) : "";
            const modes = Array.isArray(app?.signOnModes)
              ? app.signOnModes.map((m) => formatSignOnMode(m)).filter(Boolean)
              : [];
            const modesLine = modes.join(", ");
            const desc = app?.description != null ? String(app.description) : "";
            const oktaVerified = String(app?.verificationStatus || "") === "OKTA_VERIFIED";
            const logoUrl = app?.logoUrl != null ? String(app.logoUrl).trim() : "";

            return (
              <div
                key={`${rowKey}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  background: "var(--bg-elevated)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
                  ) : (
                    <span style={{ fontSize: 16, color: "var(--text-primary)" }}>
                      {displayName?.[0]?.toUpperCase() || "?"}
                    </span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "var(--text-primary)",
                    }}
                  >
                    {displayName}
                    {oktaVerified ? (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          color: "var(--accent)",
                          fontWeight: 400,
                        }}
                      >
                        ✓ Okta Verified
                      </span>
                    ) : null}
                  </div>
                  {nameKey || cat ? (
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--text-dim)",
                        fontFamily: "ui-monospace, monospace",
                        marginTop: 1,
                      }}
                    >
                      {[nameKey, cat].filter(Boolean).join(" · ")}
                    </div>
                  ) : null}
                  {modesLine ? (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-secondary)",
                        marginTop: 2,
                      }}
                    >
                      {modesLine}
                    </div>
                  ) : null}
                  {desc ? (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-dim)",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {desc}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => typeof onAddToOkta === "function" && onAddToOkta(app)}
                  disabled={isAdding || busy}
                  style={{
                    padding: "6px 14px",
                    background: isAdding ? "var(--bg-elevated)" : "var(--accent)",
                    color: isAdding ? "var(--text-dim)" : "#000",
                    border: "none",
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: isAdding ? "not-allowed" : "pointer",
                    flexShrink: 0,
                    minWidth: 90,
                  }}
                >
                  {isAdding ? "Adding..." : "Add to Okta"}
                </button>
              </div>
            );
          })}
        </div>
        {apps.length > 50 ? (
          <p className="mt-2 text-center text-[11px] text-slate-500">Showing 50 of {apps.length} results.</p>
        ) : null}
      </div>
    );
  }

  return null;
}
